import { useCallback, useEffect, useRef, useState } from 'react';

// ── Fenster: Lage, Größe, Ziehen ─────────────────────────────────────────────────────────────────
//
// Die Mechanik hinter jedem Fenster der App, an EINER Stelle. Herausgezogen aus `CallCockpit.tsx`
// (2026-08-11), als die Telefon-Tabelle dieselbe Fensterform für ihr Details- und Notiz-Fenster
// bekam. Ein zweites Mal geschrieben hiesse: zwei Klemm-Regeln, zwei Speicher-Formate, zwei
// Zieh-Verhalten — und das dritte Fenster erbte das schlechtere davon.
//
// Was hier lebt: das Klemmen an den sichtbaren Bildschirm, das Merken über den Neustart hinweg, der
// Zieh-Griff und der Beobachter für die native Größenänderung. Was NICHT hier lebt: wie das Fenster
// aussieht und wann es überhaupt eines ist — das entscheidet jede Seite selbst (das Cockpit kennt
// zusätzlich Vollbild und Zusammengeklappt).

export type Geom = { x: number; y: number; w: number; h: number };

// Unter dieser Breite gibt es kein „daneben nachschauen" — dort ist Vollbild die einzige Form, in
// der der Inhalt lesbar bleibt.
export const FENSTER_MIN_VIEWPORT = 768;
export const FENSTER_MIN_W = 380;
export const FENSTER_MIN_H = 320;

export type Ecke = 'unten-rechts' | 'mitte';
// Woran gezogen wird, um die Größe zu ändern.
export type Kante = 'rechts' | 'unten' | 'ecke';

export function standardGeom(maxW = 760, maxH = 680, ecke: Ecke = 'unten-rechts'): Geom {
  const w = Math.min(maxW, Math.max(FENSTER_MIN_W, window.innerWidth - 48));
  const h = Math.min(maxH, Math.max(FENSTER_MIN_H, window.innerHeight - 48));
  return ecke === 'mitte'
    ? { x: Math.max(0, (window.innerWidth - w) / 2), y: Math.max(0, (window.innerHeight - h) / 2), w, h }
    : { x: window.innerWidth - w - 24, y: window.innerHeight - h - 24, w, h };
}

// Immer gegen den AKTUELLEN Bildschirm klemmen, nicht nur beim Ziehen: wer gestern an zwei Monitoren
// sass und heute am Laptop sitzt, fände sein Fenster sonst ausserhalb — sichtbar nirgends,
// schliessbar nicht.
export function klemme(g: Geom): Geom {
  const w = Math.min(Math.max(g.w, FENSTER_MIN_W), window.innerWidth);
  const h = Math.min(Math.max(g.h, FENSTER_MIN_H), window.innerHeight);
  return {
    w, h,
    x: Math.min(Math.max(g.x, 0), Math.max(0, window.innerWidth - w)),
    y: Math.min(Math.max(g.y, 0), Math.max(0, window.innerHeight - h)),
  };
}

export function ladeGeom(key: string, maxW?: number, maxH?: number, ecke?: Ecke): Geom {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return standardGeom(maxW, maxH, ecke);
    const g = JSON.parse(raw) as Partial<Geom>;
    if (typeof g?.x !== 'number' || typeof g?.y !== 'number'
        || typeof g?.w !== 'number' || typeof g?.h !== 'number') return standardGeom(maxW, maxH, ecke);
    return klemme(g as Geom);
  } catch {
    return standardGeom(maxW, maxH, ecke);
  }
}

export function merkeGeom(key: string, g: Geom): void {
  try { localStorage.setItem(key, JSON.stringify(g)); } catch { /* Speicher gesperrt (privates Fenster) */ }
}

/**
 * Der ganze Fenster-Zustand für ein Bauteil: Lage, Größe, der Griff zum Ziehen und das Nachziehen
 * der nativen Größenänderung.
 *
 * `gewuenschtImFenster` ist der Wunsch des Aufrufers; zurück kommt `imFenster` — der Wunsch UND
 * genug Bildschirm dafür. Auf einem schmalen Gerät fällt jedes Fenster auf Vollbild zurück, und
 * dann schaltet der Haken auch Ziehen und Größen-Beobachter ab: sonst schriebe der Beobachter die
 * Vollbild-Maße in den Speicher und das Fenster wäre am nächsten Tag bildschirmfüllend.
 */
export function useFenster(opts: {
  key: string;
  gewuenschtImFenster: boolean;
  maxW?: number;
  maxH?: number;
  ecke?: Ecke;
}) {
  const { key, gewuenschtImFenster, maxW, maxH, ecke } = opts;
  const rootRef = useRef<HTMLDivElement>(null);
  const [geom, setGeom] = useState<Geom>(() => ladeGeom(key, maxW, maxH, ecke));
  const [schmal, setSchmal] = useState(() => window.innerWidth < FENSTER_MIN_VIEWPORT);

  useEffect(() => {
    const onResize = () => {
      setSchmal(window.innerWidth < FENSTER_MIN_VIEWPORT);
      setGeom((g) => klemme(g));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const imFenster = gewuenschtImFenster && !schmal;
  const merke = useCallback((g: Geom) => merkeGeom(key, g), [key]);

  // Spiegel des jüngsten Standes. Beim Loslassen wird DIESER gespeichert, nicht das `geom` aus dem
  // Render, in dem der Handler entstand: Zeigerbewegungen werden gebündelt verarbeitet, und der
  // letzte Schub kann nach dem Aufsetzen des Loslass-Handlers liegen — gespeichert würde dann eine
  // Lage von vor ein paar Pixeln. Ein Ref-Spiegel im Updater ist unbedenklich (idempotent), anders
  // als ein zweites setState an derselben Stelle.
  const geomRef = useRef(geom);
  geomRef.current = geom;
  const aendere = useCallback((f: (g: Geom) => Geom) => {
    setGeom((g) => { const n = f(g); geomRef.current = n; return n; });
  }, []);

  // Ziehen an der Kopfleiste. Pointer-Events statt Maus-Events, damit es auch am Touchpad-Tablet
  // funktioniert; `setPointerCapture` hält das Ziehen fest, auch wenn der Zeiger kurz das Fenster
  // verlässt (sonst „klebt" das Fenster mitten in der Bewegung fest).
  const ziehRef = useRef<{ dx: number; dy: number } | null>(null);
  const ziehGriff = {
    onPointerDown: (e: React.PointerEvent) => {
      if (!imFenster) return;
      // Knöpfe in der Kopfleiste bleiben Knöpfe — sonst zöge man das Fenster beim Schließen mit.
      if ((e.target as HTMLElement).closest('button')) return;
      ziehRef.current = { dx: e.clientX - geom.x, dy: e.clientY - geom.y };
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    },
    onPointerMove: (e: React.PointerEvent) => {
      const z = ziehRef.current;
      if (!z) return;
      aendere((g) => klemme({ ...g, x: e.clientX - z.dx, y: e.clientY - z.dy }));
    },
    onPointerUp: () => {
      if (!ziehRef.current) return;
      ziehRef.current = null;
      merke(geomRef.current);
    },
    onPointerCancel: () => {
      if (!ziehRef.current) return;
      ziehRef.current = null;
      merke(geomRef.current);
    },
  };

  // Größe ändern über EIGENE Griffe statt über CSS `resize: both` (2026-08-12).
  //
  // Der native Griff ist ein etwa 15 Pixel grosses Dreieck in der äussersten Ecke, auf dunklem
  // Grund praktisch unsichtbar — man muss wissen, dass es ihn gibt, und dann noch treffen. Die
  // eigenen Griffe liegen an der rechten Kante, an der unteren und in der Ecke, sind ein Vielfaches
  // grösser und tragen einen sichtbaren Anfasser. Nebenbei fällt der ResizeObserver weg, der die
  // vom Browser geschriebene Grösse nur noch einsammeln musste.
  const groesseRef = useRef<{ kante: Kante; x0: number; y0: number; w0: number; h0: number } | null>(null);
  const griffFuer = (kante: Kante) => ({
    onPointerDown: (e: React.PointerEvent) => {
      if (!imFenster) return;
      // Sonst zöge man beim Anfassen der unteren Kante zugleich das Fenster (Griff liegt IM Fenster).
      e.preventDefault();
      e.stopPropagation();
      groesseRef.current = { kante, x0: e.clientX, y0: e.clientY, w0: geom.w, h0: geom.h };
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    },
    onPointerMove: (e: React.PointerEvent) => {
      const s = groesseRef.current;
      if (!s) return;
      aendere((g) => klemme({
        ...g,
        w: s.kante === 'unten' ? g.w : s.w0 + (e.clientX - s.x0),
        h: s.kante === 'rechts' ? g.h : s.h0 + (e.clientY - s.y0),
      }));
    },
    onPointerUp: () => {
      if (!groesseRef.current) return;
      groesseRef.current = null;
      merke(geomRef.current);
    },
    onPointerCancel: () => {
      if (!groesseRef.current) return;
      groesseRef.current = null;
      merke(geomRef.current);
    },
  });
  const griffe = { rechts: griffFuer('rechts'), unten: griffFuer('unten'), ecke: griffFuer('ecke') };

  const fensterStil: React.CSSProperties | undefined = imFenster
    ? { left: geom.x, top: geom.y, width: geom.w, height: geom.h }
    : undefined;

  return { geom, setGeom, imFenster, schmal, rootRef, ziehGriff, griffe, fensterStil, merke };
}
